import { Home, Menu, MessageSquareDashed, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Composer } from "./components/Composer";
import { EmptyState } from "./components/EmptyState";
import { IconButton } from "./components/IconButton";
import { MessageView } from "./components/MessageView";
import { Sidebar } from "./components/Sidebar";
import { starterPrompts } from "./data";
import {
  AuthRequiredError,
  HermesChatClient,
  type ActiveChat,
  type AdapterMessage,
  type ChatEvent,
  type ChatId,
  type ConversationId,
  type ModelCatalog,
} from "./lib/hermes-chat-adapter";
import {
  defaultEffort,
  parseSelectionValue,
  selectedCapability,
  selectionValue,
  supportedReasoningEfforts,
  type ModelSelection,
  type ReasoningEffort,
} from "./lib/model-capabilities";
import type { Conversation, Message, ThemeMode } from "./types";

type ConnectionState = "connecting" | "ready" | "error";
type RegisterChatOptions = { preserveMessages?: boolean };
type ConversationDateGroup = "오늘" | "어제" | "지난 7일";

function timeLabel(timestamp = Date.now()) {
  return new Intl.DateTimeFormat("ko-KR", { hour: "numeric", minute: "2-digit" }).format(new Date(timestamp));
}

function conversationGroup(timestamp: number): ConversationDateGroup {
  const now = new Date();
  const date = new Date(timestamp);
  const startToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startDate = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.floor((startToday - startDate) / 86_400_000);
  if (days <= 0) return "오늘";
  if (days === 1) return "어제";
  return "지난 7일";
}

function updatedLabel(timestamp: number) {
  const group = conversationGroup(timestamp);
  if (group === "오늘") return timeLabel(timestamp);
  if (group === "어제") return "어제";
  return new Intl.DateTimeFormat("ko-KR", { month: "short", day: "numeric" }).format(new Date(timestamp));
}

function toConversation(item: { id: string; title: string; preview: string; updatedAt: number; source: string }): Conversation {
  return {
    id: item.id,
    title: item.title || "새로운 대화",
    preview: item.preview,
    updatedAt: updatedLabel(item.updatedAt),
    source: item.source,
  };
}

function toMessage(message: AdapterMessage): Message {
  return {
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: timeLabel(message.createdAt),
  };
}

function errorText(error: unknown) {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const record = error as Record<string, unknown>;
    return String(record.message ?? record.detail ?? record.error ?? "요청을 처리하지 못했습니다.");
  }
  return "요청을 처리하지 못했습니다.";
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error(`${file.name} 파일을 읽지 못했습니다.`));
    reader.onload = () => {
      const value = String(reader.result ?? "");
      resolve(value.includes(",") ? value.slice(value.indexOf(",") + 1) : value);
    };
    reader.readAsDataURL(file);
  });
}

export default function App({ embedded = false }: { embedded?: boolean }) {
  const embeddedLifeApp = ["dash.limjaeyoung.com", "app.limjaeyoung.com", "localhost", "127.0.0.1"].includes(window.location.hostname);
  const standaloneLifeChat = window.location.hostname === "chat.limjaeyoung.com";
  const clientRef = useRef(new HermesChatClient());
  const runtimeByConversation = useRef(new Map<ConversationId, ChatId>());
  const conversationByRuntime = useRef(new Map<ChatId, ConversationId>());
  const pendingMessageByRuntime = useRef(new Map<ChatId, string>());
  const pendingMessageByConversation = useRef(new Map<ConversationId, string>());
  const pendingPromptByConversation = useRef(new Map<ConversationId, string>());
  const activeIdRef = useRef("draft-new");
  const scrollArea = useRef<HTMLDivElement>(null);
  const shouldStick = useRef(true);
  const reconnectNowRef = useRef<() => void>(() => undefined);
  const registerActiveChatRef = useRef<((chat: ActiveChat, previousId?: string, options?: RegisterChatOptions) => ActiveChat) | null>(null);

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [messagesByConversation, setMessagesByConversation] = useState<Record<string, Message[]>>({ "draft-new": [] });
  const [activeId, setActiveId] = useState("draft-new");
  const [draft, setDraft] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(embedded);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [modelCatalog, setModelCatalog] = useState<ModelCatalog>({ provider: "", model: "", providers: [] });
  const [selectedModel, setSelectedModel] = useState<ModelSelection>({ provider: "", model: "" });
  const [effort, setEffort] = useState<ReasoningEffort>("medium");
  const [temporaryChat, setTemporaryChat] = useState(false);
  const [theme, setTheme] = useState<ThemeMode>("system");
  const [streamingConversationId, setStreamingConversationId] = useState<string | null>(null);
  const [connection, setConnection] = useState<ConnectionState>("connecting");
  const [connectionMessage, setConnectionMessage] = useState("Hermes 연결 중");
  const [displayName, setDisplayName] = useState("Jaeyoung");
  const [attachmentStatus, setAttachmentStatus] = useState<string>();

  const activeConversation = conversations.find((conversation) => conversation.id === activeId);
  const messages = messagesByConversation[activeId] ?? [];
  const messageFingerprint = messages.map((message) => `${message.id}:${message.content.length}`).join("|");
  const pageTitle = activeConversation?.title ?? "새로운 대화";
  const isEmptyConversation = messages.length === 0;
  const isStreaming = streamingConversationId === activeId;
  const reasoningEfforts = supportedReasoningEfforts(selectedModel, selectedCapability(modelCatalog, selectedModel));

  useEffect(() => { activeIdRef.current = activeId; }, [activeId]);

  const refreshConversations = async () => {
    const items = await clientRef.current.list({ limit: 100 });
    setConversations(items.map(toConversation));
  };

  const registerActiveChat = (chat: ActiveChat, previousId?: string, options: RegisterChatOptions = {}) => {
    runtimeByConversation.current.set(chat.conversationId, chat.chatId);
    conversationByRuntime.current.set(chat.chatId, chat.conversationId);
    if (previousId && previousId !== chat.conversationId) {
      const pendingAssistantId = pendingMessageByConversation.current.get(previousId);
      if (pendingAssistantId) {
        pendingMessageByConversation.current.delete(previousId);
        pendingMessageByConversation.current.set(chat.conversationId, pendingAssistantId);
      }
      const pendingPrompt = pendingPromptByConversation.current.get(previousId);
      if (pendingPrompt) {
        pendingPromptByConversation.current.delete(previousId);
        pendingPromptByConversation.current.set(chat.conversationId, pendingPrompt);
      }
      setMessagesByConversation((current) => {
        const next = { ...current, [chat.conversationId]: current[previousId] ?? chat.messages.map(toMessage) };
        delete next[previousId];
        return next;
      });
    } else if (chat.messages.length && !options.preserveMessages) {
      setMessagesByConversation((current) => ({ ...current, [chat.conversationId]: chat.messages.map(toMessage) }));
    }
    activeIdRef.current = chat.conversationId;
    setActiveId(chat.conversationId);
    const infoModel = typeof chat.info?.model === "string" ? chat.info.model : "";
    const infoProvider = typeof chat.info?.provider === "string" ? chat.info.provider : "";
    if (infoModel) {
      const provider = infoProvider || modelCatalog.providers.find((item) => item.models.includes(infoModel))?.slug || "";
      if (provider) {
        const nextSelection = { provider, model: infoModel };
        const nextEfforts = supportedReasoningEfforts(nextSelection, selectedCapability(modelCatalog, nextSelection));
        setSelectedModel(nextSelection);
        const resumedEffort = typeof chat.info?.reasoning_effort === "string"
          ? chat.info.reasoning_effort as ReasoningEffort
          : undefined;
        setEffort((current) => defaultEffort(nextEfforts, resumedEffort ?? current));
      }
    }
    return chat;
  };
  registerActiveChatRef.current = registerActiveChat;

  const getActiveChat = async (): Promise<ActiveChat> => {
    const currentId = activeIdRef.current;
    const runtimeId = runtimeByConversation.current.get(currentId);
    if (runtimeId) return { chatId: runtimeId, conversationId: currentId, messages: [], running: false };

    if (!currentId.startsWith("draft-")) {
      return registerActiveChat(await clientRef.current.resume(currentId));
    }

    const chat = await clientRef.current.create({
      provider: selectedModel.provider || undefined,
      model: selectedModel.model || undefined,
      reasoningEffort: reasoningEfforts.length ? effort : undefined,
      hidden: temporaryChat,
      closeOnDisconnect: false,
    });
    return registerActiveChat(chat, currentId);
  };

  const handleChatEvent = async (event: ChatEvent) => {
    if (event.type === "ready" || event.type === "disconnected" || event.type === "disposed") return;
    if (event.type === "error" && !event.chatId) {
      setConnection("error");
      setConnectionMessage(errorText(event.payload));
      return;
    }

    if (event.type === "approval") {
      const detail = String(event.payload.message ?? event.payload.description ?? "Hermes가 작업 실행 권한을 요청합니다. 한 번 허용할까요?");
      const approved = window.confirm(detail);
      const requestId = String(event.payload.request_id ?? event.payload.id ?? "");
      if (requestId) await clientRef.current.answerApproval(event.chatId, { requestId, choice: approved ? "once" : "deny" });
      return;
    }

    if (event.type === "clarify") {
      const question = String(event.payload.question ?? event.payload.message ?? "Hermes에 전달할 답변을 입력하세요.");
      const answer = window.prompt(question);
      const requestId = String(event.payload.request_id ?? event.payload.id ?? "");
      if (answer !== null && requestId) {
        await clientRef.current.answerClarify(event.chatId, {
          requestId,
          answer,
          questionId: typeof event.payload.question_id === "string" ? event.payload.question_id : undefined,
        });
      }
      return;
    }

    if (!("chatId" in event) || !event.chatId) return;
    const eventChatId = event.chatId;
    const conversationId = conversationByRuntime.current.get(eventChatId);
    if (!conversationId) return;
    const assistantId = pendingMessageByRuntime.current.get(eventChatId);

    if (event.type === "delta" && assistantId) {
      setMessagesByConversation((current) => ({
        ...current,
        [conversationId]: (current[conversationId] ?? []).map((message) =>
          message.id === assistantId ? { ...message, content: message.content + event.text } : message,
        ),
      }));
      return;
    }

    if (event.type === "complete" && assistantId) {
      pendingMessageByRuntime.current.delete(eventChatId);
      pendingMessageByConversation.current.delete(conversationId);
      pendingPromptByConversation.current.delete(conversationId);
      setMessagesByConversation((current) => ({
        ...current,
        [conversationId]: (current[conversationId] ?? []).map((message) =>
          message.id === assistantId
            ? { ...message, content: event.text || message.content || "응답이 완료되었습니다.", pending: false }
            : message,
        ),
      }));
      setStreamingConversationId((current) => current === conversationId ? null : current);
      window.setTimeout(() => { void refreshConversations().catch(() => undefined); }, 350);
      return;
    }

    if (event.type === "error" && assistantId) {
      pendingMessageByRuntime.current.delete(eventChatId);
      pendingMessageByConversation.current.delete(conversationId);
      pendingPromptByConversation.current.delete(conversationId);
      setMessagesByConversation((current) => ({
        ...current,
        [conversationId]: (current[conversationId] ?? []).map((message) =>
          message.id === assistantId ? { ...message, content: `요청을 처리하지 못했습니다. ${errorText(event.payload)}`, pending: false } : message,
        ),
      }));
      setStreamingConversationId((current) => current === conversationId ? null : current);
    }
  };

  useEffect(() => {
    let disposed = false;
    const abortController = new AbortController();
    const client = clientRef.current;
    const retryDelays = [0, 1_000, 2_000, 5_000, 10_000, 15_000];
    let bootstrapped = false;
    let reconnecting: Promise<void> | null = null;
    let reconnectTimer: number | undefined;
    let retryAttempt = 0;

    const clearReconnectTimer = () => {
      if (reconnectTimer === undefined) return;
      window.clearTimeout(reconnectTimer);
      reconnectTimer = undefined;
    };

    const redirectToLogin = (error: AuthRequiredError) => {
      if (embeddedLifeApp) {
        window.dispatchEvent(new CustomEvent("life:unauthorized"));
        return;
      }
      if (standaloneLifeChat) {
        window.location.assign("https://dash.limjaeyoung.com/?next=/chat");
        return;
      }
      const login = new URL(error.loginUrl, window.location.origin);
      login.searchParams.set("next", `${window.location.pathname}${window.location.search}`);
      window.location.assign(login.toString());
    };

    const restoreActiveSession = async () => {
      const previousId = activeIdRef.current;
      const pendingAssistantId = pendingMessageByConversation.current.get(previousId);
      const pendingPrompt = pendingPromptByConversation.current.get(previousId);
      runtimeByConversation.current.clear();
      conversationByRuntime.current.clear();
      pendingMessageByRuntime.current.clear();
      if (previousId.startsWith("draft-")) return;

      const chat = await client.resume(previousId);
      const preservePendingMessage = Boolean(pendingAssistantId && chat.running);
      registerActiveChatRef.current?.(chat, previousId, { preserveMessages: preservePendingMessage });
      if (pendingAssistantId && chat.running) {
        pendingMessageByRuntime.current.set(chat.chatId, pendingAssistantId);
        pendingMessageByConversation.current.set(chat.conversationId, pendingAssistantId);
        if (pendingPrompt) pendingPromptByConversation.current.set(chat.conversationId, pendingPrompt);
        setDraft("");
        setStreamingConversationId(chat.conversationId);
      } else if (pendingAssistantId) {
        const promptWasAccepted = Boolean(pendingPrompt && chat.messages.some((message) => message.role === "user" && message.content === pendingPrompt));
        pendingMessageByConversation.current.delete(previousId);
        pendingMessageByConversation.current.delete(chat.conversationId);
        pendingPromptByConversation.current.delete(previousId);
        pendingPromptByConversation.current.delete(chat.conversationId);
        if (pendingPrompt) setDraft(promptWasAccepted ? "" : pendingPrompt);
        setStreamingConversationId(null);
      }
    };

    const scheduleReconnect = (immediate = false) => {
      if (disposed || document.visibilityState === "hidden") return;
      clearReconnectTimer();
      const delay = immediate ? 0 : retryDelays[Math.min(retryAttempt++, retryDelays.length - 1)];
      reconnectTimer = window.setTimeout(() => {
        reconnectTimer = undefined;
        void connectAndRestore(true);
      }, delay);
    };

    const connectAndRestore = (force = false): Promise<void> => {
      if (disposed) return Promise.resolve();
      if (document.visibilityState === "hidden" && !force) return Promise.resolve();
      if (reconnecting) return reconnecting;

      let shouldRetry = false;
      const task = (async () => {
        try {
          setConnection("connecting");
          setConnectionMessage(bootstrapped ? "Hermes 연결 복구 중…" : "Hermes 연결 중…");
          let connectedDuringBootstrap = false;
          if (!bootstrapped) {
            const shouldConnect = force || !client.isConnected();
            const [user, catalog] = await Promise.all([
              client.authenticate(),
              client.modelOptions(),
              shouldConnect ? client.connect(abortController.signal).then(() => true) : Promise.resolve(false),
            ]);
            if (disposed) return;
            setDisplayName(user.displayName || user.email?.split("@")[0] || "Jaeyoung");
            const initialSelection = { provider: catalog.provider, model: catalog.model };
            const initialEfforts = supportedReasoningEfforts(initialSelection, selectedCapability(catalog, initialSelection));
            setModelCatalog(catalog);
            setSelectedModel(initialSelection);
            setEffort(defaultEffort(initialEfforts, "medium"));
            connectedDuringBootstrap = shouldConnect;
            bootstrapped = true;
          }

          if (!connectedDuringBootstrap && (force || !client.isConnected())) await client.connect(abortController.signal);
          if (disposed) return;
          try {
            await restoreActiveSession();
          } catch (error) {
            if (!client.isConnected()) throw error;
            setStreamingConversationId(null);
            setConnectionMessage(`Hermes 연결됨 · ${errorText(error)}`);
          }
          if (disposed) return;
          retryAttempt = 0;
          setConnection("ready");
          setConnectionMessage("Hermes 연결됨");
          void refreshConversations().catch(() => undefined);
        } catch (error) {
          if (disposed || (error instanceof DOMException && error.name === "AbortError")) return;
          if (error instanceof AuthRequiredError) {
            redirectToLogin(error);
            return;
          }
          setConnection("connecting");
          setConnectionMessage(`${errorText(error)} 자동으로 다시 연결하는 중…`);
          shouldRetry = true;
        }
      })();

      reconnecting = task.finally(() => {
        reconnecting = null;
        if (shouldRetry) scheduleReconnect();
      });
      return reconnecting;
    };

    reconnectNowRef.current = () => {
      retryAttempt = 0;
      clearReconnectTimer();
      void connectAndRestore(true);
    };

    const consumeEvents = async () => {
      for await (const event of client.events()) {
        if (disposed) break;
        if (event.type === "disposed") break;
        if (event.type === "disconnected") {
          setConnection("connecting");
          setConnectionMessage("Hermes 연결 복구 중…");
          scheduleReconnect(true);
          continue;
        }
        try { await handleChatEvent(event); }
        catch (error) { setConnectionMessage(errorText(error)); }
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState !== "visible" || !bootstrapped) return;
      retryAttempt = 0;
      clearReconnectTimer();
      void connectAndRestore(true);
    };
    const handleOnline = () => {
      retryAttempt = 0;
      clearReconnectTimer();
      void connectAndRestore(true);
    };
    const handleOffline = () => {
      clearReconnectTimer();
      setConnection("connecting");
      setConnectionMessage("네트워크를 기다리는 중…");
    };
    const handlePageShow = (event: PageTransitionEvent) => {
      if (!event.persisted || !bootstrapped) return;
      retryAttempt = 0;
      void connectAndRestore(true);
    };
    const heartbeat = window.setInterval(() => {
      if (disposed || document.visibilityState !== "visible" || reconnecting || !client.isConnected()) return;
      void client.probe().catch(() => { void connectAndRestore(true); });
    }, 25_000);

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    window.addEventListener("pageshow", handlePageShow);
    void consumeEvents();
    void connectAndRestore(true);
    return () => {
      disposed = true;
      reconnectNowRef.current = () => undefined;
      clearReconnectTimer();
      window.clearInterval(heartbeat);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      window.removeEventListener("pageshow", handlePageShow);
      abortController.abort();
      client.dispose();
    };
  }, []);

  const openSidebar = () => {
    setSidebarCollapsed(false);
    setDrawerOpen(true);
  };

  const closeSidebar = () => {
    setDrawerOpen(false);
    if (window.matchMedia("(min-width: 861px)").matches) setSidebarCollapsed(true);
  };

  const createConversation = (useTemporary = temporaryChat) => {
    const id = `draft-${Date.now()}`;
    activeIdRef.current = id;
    setMessagesByConversation((current) => ({ ...current, [id]: [] }));
    setActiveId(id);
    setTemporaryChat(useTemporary);
    setDraft("");
    setAttachmentStatus(undefined);
    setDrawerOpen(false);
  };

  const toggleTemporaryChat = () => {
    const next = !temporaryChat;
    setTemporaryChat(next);
    createConversation(next);
  };

  const selectConversation = async (conversationId: string) => {
    if (conversationId === activeIdRef.current) {
      setDrawerOpen(false);
      return;
    }
    activeIdRef.current = conversationId;
    setActiveId(conversationId);
    setTemporaryChat(false);
    setDrawerOpen(false);
    setAttachmentStatus(undefined);
    if (messagesByConversation[conversationId]?.length) return;
    try {
      const chat = registerActiveChat(await clientRef.current.resume(conversationId));
      let history = chat.messages;
      if (!history.length) history = await clientRef.current.history(chat.conversationId);
      setMessagesByConversation((current) => ({ ...current, [chat.conversationId]: history.map(toMessage) }));
    } catch (error) {
      setConnectionMessage(errorText(error));
    }
  };

  const sendMessage = async () => {
    const content = draft.trim();
    if (!content || connection !== "ready" || isStreaming) return;
    setDraft("");
    shouldStick.current = true;
    try {
      const chat = await getActiveChat();
      const now = Date.now();
      const userMessage: Message = { id: `user-${now}`, role: "user", content, createdAt: timeLabel(now) };
      const assistantId = `assistant-${now + 1}`;
      const assistantMessage: Message = { id: assistantId, role: "assistant", content: "", createdAt: timeLabel(now), pending: true };
      pendingMessageByRuntime.current.set(chat.chatId, assistantId);
      pendingMessageByConversation.current.set(chat.conversationId, assistantId);
      pendingPromptByConversation.current.set(chat.conversationId, content);
      setMessagesByConversation((current) => ({
        ...current,
        [chat.conversationId]: [...(current[chat.conversationId] ?? []), userMessage, assistantMessage],
      }));
      setStreamingConversationId(chat.conversationId);
      if (!temporaryChat) {
        setConversations((current) => current.some((item) => item.id === chat.conversationId)
          ? current
          : [{ id: chat.conversationId, title: content.slice(0, 32), preview: content, updatedAt: "방금", source: "webui" }, ...current]);
      }
      await clientRef.current.send(chat.chatId, content);
      setAttachmentStatus(undefined);
    } catch (error) {
      setStreamingConversationId(null);
      setDraft(content);
      setConnectionMessage(errorText(error));
    }
  };

  const stopStreaming = async () => {
    const runtimeId = runtimeByConversation.current.get(activeIdRef.current);
    if (!runtimeId) return;
    try { await clientRef.current.stop(runtimeId); }
    catch (error) { setConnectionMessage(errorText(error)); }
  };

  const attachFiles = async (files: FileList) => {
    if (connection !== "ready") return;
    const images = Array.from(files).filter((file) => file.type.startsWith("image/"));
    if (!images.length) {
      setAttachmentStatus("현재는 이미지 파일만 첨부할 수 있습니다.");
      return;
    }
    setAttachmentStatus("이미지 첨부 중…");
    try {
      const chat = await getActiveChat();
      for (const file of images) {
        await clientRef.current.attachImage(chat.chatId, { contentBase64: await fileToBase64(file), filename: file.name });
      }
      setAttachmentStatus(`${images.length}개 이미지가 다음 메시지에 첨부됩니다.`);
    } catch (error) {
      setAttachmentStatus(errorText(error));
    }
  };

  const selectModel = async (value: string) => {
    const next = parseSelectionValue(value);
    const nextEfforts = supportedReasoningEfforts(next, selectedCapability(modelCatalog, next));
    const previousModel = selectedModel;
    const previousEffort = effort;
    const nextEffort = defaultEffort(nextEfforts, effort);
    setSelectedModel(next);
    setEffort(nextEffort);

    const runtimeId = runtimeByConversation.current.get(activeIdRef.current);
    if (!runtimeId) return;
    try {
      await clientRef.current.setModel(runtimeId, next);
      if (nextEfforts.length) await clientRef.current.setReasoning(runtimeId, nextEffort);
      setConnectionMessage(isStreaming ? `${next.model} · 다음 메시지부터 적용` : `${next.model} · 적용됨`);
    } catch (error) {
      setSelectedModel(previousModel);
      setEffort(previousEffort);
      setConnectionMessage(errorText(error));
    }
  };

  const selectEffort = async (next: ReasoningEffort) => {
    const previous = effort;
    setEffort(next);
    const runtimeId = runtimeByConversation.current.get(activeIdRef.current);
    if (!runtimeId) return;
    try {
      await clientRef.current.setReasoning(runtimeId, next);
      setConnectionMessage(`${next} 추론 · 적용됨`);
    } catch (error) {
      setEffort(previous);
      setConnectionMessage(errorText(error));
    }
  };

  useEffect(() => { document.title = `${pageTitle} · Hermes`; }, [pageTitle]);

  useEffect(() => {
    if (!shouldStick.current) return;
    const area = scrollArea.current;
    if (area) area.scrollTop = area.scrollHeight;
  }, [messageFingerprint]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if (!(event.metaKey || event.ctrlKey)) return;
      if (event.key.toLowerCase() === "n") { event.preventDefault(); createConversation(); }
      if (event.key.toLowerCase() === "k") {
        event.preventDefault();
        openSidebar();
        window.setTimeout(() => document.querySelector<HTMLInputElement>("#conversation-search")?.focus(), 80);
      }
    };
    window.addEventListener("keydown", handleShortcut);
    return () => window.removeEventListener("keydown", handleShortcut);
  });

  return (
    <div className={`chat-feature ${embedded ? "is-embedded" : ""} ${sidebarCollapsed ? "sidebar-collapsed" : ""}`} data-theme={theme === "system" ? undefined : theme}>
      <button className={`drawer-backdrop ${drawerOpen ? "is-visible" : ""}`} type="button" aria-label="대화 목록 닫기" onClick={() => setDrawerOpen(false)} />
      <Sidebar
        conversations={conversations}
        activeId={activeId}
        isOpen={drawerOpen}
        onClose={closeSidebar}
        onNewChat={() => createConversation()}
        onSelect={(id) => { void selectConversation(id); }}
        onOpenSettings={() => setSettingsOpen(true)}
        displayName={displayName}
        connectionLabel={connectionMessage}
      />

      <main className={`chat-layout ${isEmptyConversation ? "is-empty" : ""}`}>
        <header className="topbar">
          <div className="topbar__start">
            <IconButton label="대화 목록 열기" onClick={openSidebar} className="menu-button liquid-circle"><Menu size={20} /></IconButton>
            <select
              className="model-selector"
              value={selectionValue(selectedModel)}
              onChange={(event) => {
                const value = event.currentTarget.value;
                void selectModel(value);
              }}
              aria-label="모델 선택"
              disabled={!modelCatalog.providers.length}
            >
              {!modelCatalog.providers.length && <option value="|">모델 불러오는 중…</option>}
              {modelCatalog.providers.map((provider) => (
                <optgroup key={provider.slug} label={provider.name}>
                  {provider.models.map((model) => (
                    <option key={`${provider.slug}:${model}`} value={selectionValue({ provider: provider.slug, model })}>{model}</option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <strong className="topbar__title">{pageTitle}</strong>
          <div className="topbar__end">
            <a className="app-home-button" href={embeddedLifeApp ? "/" : "https://dash.limjaeyoung.com/"} aria-label="Life 대시보드"><Home size={19} /></a>
            <button type="button" className={`temporary-chat-button ${temporaryChat ? "is-active" : ""}`} onClick={toggleTemporaryChat} aria-pressed={temporaryChat} aria-label={temporaryChat ? "임시 채팅 종료" : "임시 채팅 시작"}>
              <MessageSquareDashed size={19} /><span>{temporaryChat ? "임시 채팅 중" : "임시 채팅"}</span>
            </button>
          </div>
        </header>

        {temporaryChat && <div className="temporary-banner">이 대화는 기록에 저장되지 않습니다.</div>}
        {connection !== "ready" && (
          <div className={`connection-banner ${connection === "connecting" ? "is-reconnecting" : ""}`} role={connection === "error" ? "alert" : "status"}>
            <span>{connectionMessage}</span>
            <button type="button" onClick={() => reconnectNowRef.current()}>지금 재연결</button>
          </div>
        )}

        <div className="message-scroll" ref={scrollArea} onScroll={(event) => {
          const element = event.currentTarget;
          shouldStick.current = element.scrollHeight - element.scrollTop - element.clientHeight < 160;
        }}>
          <div className={`transcript ${isEmptyConversation ? "transcript--empty" : ""}`}>
            {isEmptyConversation
              ? <EmptyState />
              : messages.map((message) => <MessageView key={message.id} message={message} onRetry={message.role === "assistant" ? () => setDraft("직전 답변을 다시 작성해줘") : undefined} />)}
          </div>
        </div>

        <Composer
          value={draft}
          isStreaming={isStreaming}
          disabled={connection !== "ready"}
          effort={effort}
          effortOptions={reasoningEfforts}
          attachmentStatus={attachmentStatus}
          isEmptyConversation={isEmptyConversation}
          suggestions={starterPrompts.map((prompt) => prompt.text)}
          onChange={setDraft}
          onEffortChange={(next) => { void selectEffort(next); }}
          onAttachFiles={(files) => { void attachFiles(files); }}
          onSend={() => { void sendMessage(); }}
          onStop={() => { void stopStreaming(); }}
          onSelectSuggestion={setDraft}
        />
      </main>

      {settingsOpen && (
        <div className="settings-popover liquid-control" role="dialog" aria-label="화면 설정">
          <div className="settings-popover__header"><div><small>보기 설정</small><strong>테마</strong></div><IconButton label="설정 닫기" onClick={() => setSettingsOpen(false)}><X size={18} /></IconButton></div>
          <label className="theme-select-label" htmlFor="theme-select">화면 테마</label>
          <select
            id="theme-select"
            className="theme-select"
            value={theme}
            onChange={(event) => {
              const value = event.currentTarget.value as ThemeMode;
              setTheme(value);
            }}
          >
            <option value="system">시스템 설정 사용</option>
            <option value="light">라이트</option>
            <option value="dark">다크</option>
          </select>
        </div>
      )}
    </div>
  );
}
